import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { ProjectDetail, ProjectSummary } from '@/types'

import RichText from '@/components/cms/rich-text'
import { motion } from 'motion/react'
import { enter } from '@/lib/motion'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/motion/reveal'
import { SplitText } from '@/components/motion/split-text'
import { ArrowLink } from '@/components/site/arrow-link'
import { WorkCard } from '@/components/site/work-card'

import {
    getProjectBySlug,
    getProjectSlugs,
    getProjects
} from '@/lib/content'

interface ProjectPageProps {
    project: ProjectDetail
    more: ProjectSummary[]
}

type GalleryImage = {
    src: string
    alt: string
}

const ProjectPage: FC<ProjectPageProps> = ({ project, more }): ReactNode => {
    const gallery = [
        { src: project.imageOne, alt: project.imageOneAlt },
        { src: project.imageTwo, alt: project.imageTwoAlt },
        { src: project.imageThree, alt: project.imageThreeAlt }
    ].filter((image) => Boolean(image.src)).map((image) => ({ src: image.src ?? '', alt: image.alt ?? '' })) as GalleryImage[]

    return (
        <Layout>
            <Seo
                title={project.client}
                description={project.summary ?? `${project.title} for ${project.client}, a project by Zaki Hatta.`}
                ogImage={project.cover ?? undefined}
            />

            <section
                id='project'
                className='relative z-[2] flex w-full flex-col bg-background px-4 py-section-sm lg:px-6'
            >
                <div className='flex w-full items-start gap-12'>
                    <div className='flex w-full py-4 pr-4 md:max-w-[68%] lg:max-w-[40%] lg:flex-1 lg:py-6 lg:pr-6'>
                        <ArrowLink href='/work' label="Back to work" direction='left' />
                    </div>

                    <div aria-hidden='true' className='hidden flex-1 lg:block' />
                </div>

                <div className='flex w-full flex-col items-start gap-12 lg:flex-row'>
                    <div className='relative z-[1] flex w-full flex-col gap-6 md:max-w-[68%] md:gap-12 lg:sticky lg:top-[240px] lg:max-w-[32%] lg:flex-1 lg:gap-20 lg:pr-6 lg:pt-6'>
                        <div className='overflow-clip'>
                            <motion.h1
                                data-mdlfy-cms={`projects:${project.id}:title`}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={enter(0.1)}
                                className='t-name max-w-[600px] text-left text-foreground'
                            >
                                {project.title}
                            </motion.h1>
                        </div>

                        <motion.dl
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={enter(0.2)}
                            className='flex flex-col gap-2 text-foreground'
                        >
                            <div className='flex items-center gap-6'>
                                <dt className='t-italic max-w-[32%] flex-1 text-left'>
                                    Project
                                </dt>

                                <dd className='t-body flex-1 text-left'>
                                    <span data-mdlfy-cms={`projects:${project.id}:client`}>
                                        {project.client}
                                    </span>
                                </dd>
                            </div>

                            {project.role && (
                                <div className='flex items-center gap-6'>
                                    <dt className='t-italic max-w-[32%] flex-1 text-left'>
                                        Role
                                    </dt>

                                    <dd className='t-body flex-1 text-left'>
                                        <span data-mdlfy-cms={`projects:${project.id}:role`}>
                                            {project.role}
                                        </span>
                                    </dd>
                                </div>
                            )}

                            {project.category && (
                                <div className='flex items-center gap-6'>
                                    <dt className='t-italic max-w-[32%] flex-1 text-left'>
                                        Category
                                    </dt>

                                    <dd className='t-body flex-1 text-left uppercase'>
                                        <span data-mdlfy-cms={`categories:${project.category.id}:name`}>
                                            {project.category.name}
                                        </span>
                                    </dd>
                                </div>
                            )}

                            <div className='flex items-center gap-6'>
                                <dt className='t-italic max-w-[32%] flex-1 text-left'>
                                    Year
                                </dt>

                                <dd className='t-body flex-1 text-left'>
                                    <span data-mdlfy-cms={`projects:${project.id}:year`}>
                                        {project.year}
                                    </span>
                                </dd>
                            </div>

                            {project.stack && (
                                <div className='flex items-start gap-6'>
                                    <dt className='t-italic max-w-[32%] flex-1 pt-1 text-left'>
                                        Stack
                                    </dt>

                                    <dd className='t-body flex-1 text-left'>
                                        <span data-mdlfy-cms={`projects:${project.id}:stack`}>
                                            {project.stack}
                                        </span>
                                    </dd>
                                </div>
                            )}
                        </motion.dl>

                        {project.story && (
                            <motion.div
                                initial={{ opacity: 0, y: 16 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={enter(0.3)}
                                className='flex flex-col gap-6 text-foreground'
                            >
                                <h2 className='t-italic text-left'>
                                    About the project
                                </h2>

                                <RichText html={project.story} className='max-w-[600px]' />
                            </motion.div>
                        )}
                    </div>

                    <div className='flex w-full flex-col gap-6 lg:flex-1'>
                        {project.cover && (
                            <Reveal y={20}>
                                <div className='relative aspect-[1.41562] w-full overflow-hidden bg-muted'>
                                    <img
                                        src={project.cover}
                                        alt={project.coverAlt ?? ''}
                                        decoding='async'
                                        className='absolute inset-0 size-full object-cover object-[50%_40%]'
                                    />
                                </div>
                            </Reveal>
                        )}

                        {gallery.map((image) => (
                            <Reveal key={image.src} y={20}>
                                <div className='relative aspect-[1.54626] w-full overflow-hidden bg-muted'>
                                    <img
                                        src={image.src}
                                        alt={image.alt}
                                        loading='lazy'
                                        decoding='async'
                                        className='absolute inset-0 size-full object-cover object-[50%_30%]'
                                    />
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {more.length > 0 && (
                <section
                    id='more-work'
                    className='relative z-[2] flex w-full flex-col items-start gap-12 overflow-clip bg-background px-4 pb-section-lg pt-section-sm md:items-center md:gap-20 lg:px-6'
                >
                    <h2
                        aria-label="Want to see more"
                        className='flex w-min flex-col items-start text-foreground'
                    >
                        <SplitText
                            text="Want"
                            effect='slide'
                            accessible={false}
                            className='t-name block whitespace-pre'
                        />

                        {' '}

                        <span className='flex items-center gap-4'>
                            <SplitText
                                text="to"
                                effect='fade'
                                accessible={false}
                                className='t-number block whitespace-pre'
                            />

                            {' '}

                            <SplitText
                                text="See more"
                                effect='slide'
                                delay={0.1}
                                accessible={false}
                                className='t-name block whitespace-pre'
                            />
                        </span>
                    </h2>

                    <div className='flex w-full flex-col items-start gap-6 md:flex-row'>
                        {more.map((item, index) => (
                            <Reveal key={item.id} className='w-full md:flex-1' delay={index * 0.08}>
                                <WorkCard project={item} />
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getProjectSlugs()

        return { paths: slugs.map((slug) => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Project paths could not be loaded:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ProjectPageProps, { slug: string }> = async ({ params }) => {
    const slug = params?.slug

    if (!slug) return { notFound: true }

    try {
        const [project, projects] = await Promise.all([getProjectBySlug(slug), getProjects()])

        if (!project) return { notFound: true, revalidate: 60 }

        const position = projects.findIndex((item) => item.slug === slug)
        const others = [...projects.slice(position + 1), ...projects.slice(0, Math.max(position, 0))]
        const more = others.filter((item) => item.slug !== slug).slice(0, 2)

        return { props: { project, more }, revalidate: 60 }
    }
    catch (error) {
        console.error('Project could not be loaded:', error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 10 }
    }
}

export default ProjectPage
